"use client";

import {
  type InfiniteData,
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import {
  type ActivityCommentLikeListResponse,
  type ActivityPostCommentItem,
  type ActivityPostCommentListResponse,
  createActivityPostComment,
  deleteActivityPostComment,
  listActivityCommentLikes,
  listActivityPostComments,
  setActivityPostCommentPinned,
  toggleActivityPostCommentLike,
  toggleActivityPostLike,
} from "@/actions/activity-post";
import {
  type CommentThreadPosition,
  createOptimisticCommentId,
  deriveOptimisticCommentThreadPosition,
  getCommentSubtreeIds,
} from "@/lib/comments/threading";
import {
  type MusicAttachment,
  musicAttachmentInput,
} from "@/lib/music/attachment";
import { PROFILE_REPLIES_ROOT_KEY } from "@/lib/query-keys/profile-tabs";
import type { CreateActivityPostCommentInput } from "@/validations/activity-post";

type CommentViewerInfo = {
  id?: string;
  displayName?: string | null;
  username?: string | null;
  avatarUrl?: string | null;
};

type CreateActivityCommentPayload = Omit<
  CreateActivityPostCommentInput,
  "activityPostId" | "music"
> & {
  activityPostId: number;
  viewer?: CommentViewerInfo;
  song?: MusicAttachment | null;
};

type CommentMeta = {
  tempId?: string;
  isPending?: boolean;
};

type CommentsPage = Omit<ActivityPostCommentListResponse, "comments"> & {
  comments: Array<ActivityPostCommentItem & CommentMeta>;
};

type CommentsInfiniteData = InfiniteData<CommentsPage, number | null>;

type ToggleCommentLikeVariables = {
  commentId: number;
  activityPostId: number;
  currentlyLiked: boolean;
};

export const activityCommentsKeys = {
  comments: (activityPostId: number) =>
    ["activityPosts", "comments", activityPostId] as const,
  commentLikes: (commentId: number) =>
    ["activityPosts", "comment-likes", commentId] as const,
};

export function useActivityComments(
  activityPostId: number | null,
  options?: {
    enabled?: boolean;
    limit?: number;
  },
) {
  const { enabled = true, limit = 100 } = options ?? {};

  return useInfiniteQuery({
    queryKey: activityCommentsKeys.comments(activityPostId ?? 0),
    enabled: Boolean(activityPostId && enabled),
    queryFn: async ({ pageParam }) => {
      if (!activityPostId) {
        return {
          comments: [],
          hasMore: false,
          nextCursor: null,
        } satisfies CommentsPage;
      }

      const result = await listActivityPostComments({
        activityPostId,
        cursor: pageParam ?? undefined,
        limit,
      });

      if (!result.ok) {
        throw new Error(result.message ?? "Failed to fetch comments");
      }

      return {
        ...result.data,
        comments: result.data.comments.map((comment) => ({
          ...comment,
          isPending: false,
        })),
      } satisfies CommentsPage;
    },
    initialPageParam: null as number | null,
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.nextCursor : undefined,
    staleTime: 15_000,
    // Other people change this list, and notifications link straight into
    // it; with `false` the 24h cache served it stale until an app restart.
    refetchOnMount: true,
    placeholderData: keepPreviousData,
  });
}

export function useCreateActivityCommentMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["activityPosts", "comments", "create"],
    mutationFn: async (payload: CreateActivityCommentPayload) => {
      const result = await createActivityPostComment({
        activityPostId: payload.activityPostId,
        content: payload.content,
        parentCommentId: payload.parentCommentId ?? null,
        voice: payload.voice ?? null,
        media: payload.media ?? null,
        music: payload.song ? musicAttachmentInput(payload.song) : null,
      });
      if (!result.ok) {
        throw new Error(result.message ?? "Failed to create comment");
      }
      return { id: result.data.id, payload };
    },
    onMutate: async (payload) => {
      const key = activityCommentsKeys.comments(payload.activityPostId);
      await queryClient.cancelQueries({ queryKey: key });

      const prev = queryClient.getQueryData<CommentsInfiniteData>(key);
      const threadPosition = deriveOptimisticCommentThreadPosition(
        payload.parentCommentId,
        prev?.pages,
      );
      const optimistic = threadPosition
        ? buildOptimisticActivityComment(payload, threadPosition)
        : null;

      if (optimistic) {
        queryClient.setQueryData<CommentsInfiniteData>(key, (old) =>
          prependComment(old, optimistic),
        );
      }

      return {
        prev,
        optimisticId: optimistic?.tempId,
        activityPostId: payload.activityPostId,
      };
    },
    onError: (_err, payload, ctx) => {
      if (ctx?.prev) {
        queryClient.setQueryData(
          activityCommentsKeys.comments(payload.activityPostId),
          ctx.prev,
        );
      }
    },
    onSuccess: (result, payload, ctx) => {
      const key = activityCommentsKeys.comments(payload.activityPostId);
      queryClient.setQueryData<CommentsInfiniteData>(key, (old) =>
        replaceCommentId(old, ctx?.optimisticId, result.id),
      );
    },
    onSettled: (_data, _error, payload) => {
      queryClient.invalidateQueries({
        queryKey: activityCommentsKeys.comments(payload.activityPostId),
      });
      // Your replies tab lists this comment too.
      queryClient.invalidateQueries({ queryKey: PROFILE_REPLIES_ROOT_KEY });
    },
  });
}

export function useDeleteActivityCommentMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["activityPosts", "comments", "delete"],
    mutationFn: async ({ id }: { id: number; activityPostId: number }) => {
      const result = await deleteActivityPostComment({ id });
      if (!result.ok) {
        throw new Error(result.message ?? "Failed to delete comment");
      }
      return { id };
    },
    onMutate: async ({
      id,
      activityPostId,
    }: {
      id: number;
      activityPostId: number;
    }) => {
      const key = activityCommentsKeys.comments(activityPostId);
      await queryClient.cancelQueries({ queryKey: key });
      const prev = queryClient.getQueryData<CommentsInfiniteData>(key);

      queryClient.setQueryData<CommentsInfiniteData>(key, (old) =>
        removeComment(old, id),
      );

      return { prev, activityPostId };
    },
    onError: (_err, vars, ctx) => {
      if (ctx?.prev) {
        queryClient.setQueryData(
          activityCommentsKeys.comments(vars.activityPostId),
          ctx.prev,
        );
      }
    },
    onSettled: (_data, _error, vars) => {
      queryClient.invalidateQueries({
        queryKey: activityCommentsKeys.comments(vars.activityPostId),
      });
    },
  });
}

export function useToggleActivityCommentLikeMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["activityPosts", "comments", "toggle-like"],
    mutationFn: async ({ commentId }: ToggleCommentLikeVariables) => {
      const result = await toggleActivityPostCommentLike({ commentId });
      if (!result.ok) {
        throw new Error(result.message ?? "Failed to toggle like");
      }
      return { liked: result.data.liked };
    },
    onMutate: async ({
      commentId,
      activityPostId,
      currentlyLiked,
    }: ToggleCommentLikeVariables) => {
      const key = activityCommentsKeys.comments(activityPostId);
      await queryClient.cancelQueries({ queryKey: key });
      const prev = queryClient.getQueryData<CommentsInfiniteData>(key);

      queryClient.setQueryData<CommentsInfiniteData>(key, (old) =>
        updateComment(old, commentId, (comment) => ({
          ...comment,
          isLikedByViewer: !currentlyLiked,
          likeCount: Math.max(0, comment.likeCount + (currentlyLiked ? -1 : 1)),
        })),
      );

      return { prev, activityPostId };
    },
    onError: (_err, vars, ctx) => {
      if (!ctx?.prev) return;
      queryClient.setQueryData(
        activityCommentsKeys.comments(vars.activityPostId),
        ctx.prev,
      );
    },
    onSettled: (_data, _error, vars) => {
      queryClient.invalidateQueries({
        queryKey: activityCommentsKeys.comments(vars.activityPostId),
      });
    },
  });
}

export function useSetActivityCommentPinnedMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["activityPosts", "comments", "pin"],
    mutationFn: async (input: {
      activityPostId: number;
      commentId: number;
      isPinned: boolean;
    }) => {
      const result = await setActivityPostCommentPinned(input);
      if (!result.ok) throw new Error(result.message);
      return result.data;
    },
    onSettled: (_data, _error, input) => {
      queryClient.invalidateQueries({
        queryKey: activityCommentsKeys.comments(input.activityPostId),
      });
    },
  });
}

type ActivityCommentLikesPage = ActivityCommentLikeListResponse;

export function useActivityCommentLikes(
  commentId: number | null,
  options?: {
    enabled?: boolean;
    limit?: number;
  },
) {
  const { enabled = true, limit = 30 } = options ?? {};

  return useInfiniteQuery({
    queryKey: activityCommentsKeys.commentLikes(commentId ?? 0),
    enabled: Boolean(commentId && enabled),
    queryFn: async ({ pageParam }) => {
      if (!commentId) {
        return {
          likes: [],
          hasMore: false,
          nextCursor: null,
        } satisfies ActivityCommentLikesPage;
      }

      const result = await listActivityCommentLikes({
        commentId,
        cursor: pageParam ?? undefined,
        limit,
      });

      if (!result.ok) {
        throw new Error(result.message ?? "Failed to fetch comment likes");
      }

      return result.data;
    },
    initialPageParam: null as ActivityCommentLikesPage["nextCursor"],
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.nextCursor : undefined,
    staleTime: 15_000,
    // Other people change this list, and notifications link straight into
    // it; with `false` the 24h cache served it stale until an app restart.
    refetchOnMount: true,
    placeholderData: keepPreviousData,
  });
}

// Helper functions
function buildOptimisticActivityComment(
  payload: CreateActivityCommentPayload,
  threadPosition: CommentThreadPosition,
): ActivityPostCommentItem & CommentMeta {
  const id = createOptimisticCommentId();
  const tempId = `temp-comment-${Math.abs(id)}`;
  const authorName =
    payload.viewer?.displayName || payload.viewer?.username || "You";

  return {
    id,
    tempId,
    isPending: true,
    activityPostId: payload.activityPostId,
    parentCommentId: threadPosition.parentCommentId,
    rootCommentId: threadPosition.rootCommentId,
    depth: threadPosition.depth,
    content: payload.content,
    voice: payload.voice ?? null,
    media: payload.media ?? null,
    music: payload.song ?? null,
    isPinned: false,
    pinnedAt: null,
    createdAt: new Date(),
    author: {
      id: payload.viewer?.id ?? "temp",
      displayName: authorName,
      username: payload.viewer?.username ?? null,
      avatarUrl: payload.viewer?.avatarUrl ?? null,
    },
    likeCount: 0,
    isLikedByViewer: false,
  };
}

function prependComment(
  data: CommentsInfiniteData | undefined,
  comment: ActivityPostCommentItem & CommentMeta,
): CommentsInfiniteData | undefined {
  if (!data) return data;
  const [first, ...rest] = data.pages;
  if (!first) return data;

  const deduped = first.comments.filter(
    (existing) =>
      (existing.tempId ?? existing.id) !== (comment.tempId ?? comment.id),
  );
  const nextFirst: CommentsPage = {
    ...first,
    comments: [comment, ...deduped],
  };

  return {
    ...data,
    pages: [nextFirst, ...rest],
  };
}

function replaceCommentId(
  data: CommentsInfiniteData | undefined,
  tempId: string | undefined,
  newId: number,
): CommentsInfiniteData | undefined {
  if (!data || !tempId) return data;

  const pages = data.pages.map((page) => {
    const comments = page.comments.map((comment) => {
      if (comment.tempId !== tempId) return comment;
      return {
        ...comment,
        id: newId,
        tempId: undefined,
        isPending: false,
      };
    });
    return { ...page, comments };
  });

  return { ...data, pages };
}

function removeComment(
  data: CommentsInfiniteData | undefined,
  id: number,
): CommentsInfiniteData | undefined {
  if (!data) return data;

  const removedIds = getCommentSubtreeIds(
    data.pages.flatMap((page) => page.comments),
    id,
  );

  const pages = data.pages.map((page) => ({
    ...page,
    comments: page.comments.filter((comment) => !removedIds.has(comment.id)),
  }));

  return { ...data, pages };
}

function updateComment(
  data: CommentsInfiniteData | undefined,
  commentId: number,
  updater: (
    comment: ActivityPostCommentItem & CommentMeta,
  ) => ActivityPostCommentItem & CommentMeta,
): CommentsInfiniteData | undefined {
  if (!data) return data;

  const pages = data.pages.map((page) => ({
    ...page,
    comments: page.comments.map((comment) =>
      comment.id === commentId ? updater(comment) : comment,
    ),
  }));

  return { ...data, pages };
}

// ─────────────────────────────────────────────────────────────────────────────
// TOGGLE ACTIVITY POST LIKE MUTATION
// ─────────────────────────────────────────────────────────────────────────────

type ToggleActivityPostLikeVariables = {
  activityPostId: number;
  currentlyLiked: boolean;
};

export function useToggleActivityPostLikeMutation() {
  return useMutation({
    mutationKey: ["activityPosts", "toggle-like"],
    mutationFn: async ({ activityPostId }: ToggleActivityPostLikeVariables) => {
      const result = await toggleActivityPostLike({ activityPostId });
      if (!result.ok) {
        throw new Error(result.message ?? "Failed to toggle like");
      }
      return { liked: result.data.liked };
    },
    onMutate: async ({
      activityPostId,
      currentlyLiked,
    }: ToggleActivityPostLikeVariables) => {
      // We don't have a feed cache for activity posts like regular posts,
      // so we just track the previous state for potential rollback
      return { activityPostId, previousLiked: currentlyLiked };
    },
    onError: (_err, _vars, _ctx) => {
      // Rollback is handled by the component via the error callback
    },
  });
}
